/**
 * Subscription Plan Card Component - Beautiful plan display with features and pricing
 */
import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/currencyFormatter';

/**
 * Format price for display
 */
const formatPrice = (price, currency = 'USD') => {
  if (price === 0) return 'Free';
  return formatCurrency(price, { currencyCode: currency });
};

/**
 * Format usage limit for display
 */
const formatLimit = (limit) => {
  if (limit === -1) return 'Unlimited';
  if (limit >= 1000) {
    return `${(limit / 1000).toFixed(0)}K`;
  }
  return limit.toLocaleString();
};

// Known plan feature flags we expose in UI
const FEATURE_FLAGS = [
  { id: 'loyalty_points', name: 'Loyalty points' },
  { id: 'whatsapp_bill_share', name: 'WhatsApp bill share' },
  { id: 'ecommerce_website', name: 'E-commerce website' },
];

/**
 * Get plan features array from limits + plan feature flags
 */
const getPlanFeatures = (features, limits) => {
  const featureList = [];

  // Add usage limits as features
  if (limits) {
    if (limits.maxUsers !== undefined && limits.maxUsers !== null) {
      featureList.push({
        name: `${formatLimit(limits.maxUsers)} Users`,
        included: true,
        highlight: limits.maxUsers === -1
      });
    }

    if (limits.maxLocations !== undefined && limits.maxLocations !== null) {
      featureList.push({
        name: `${formatLimit(limits.maxLocations)} Locations`,
        included: true,
        highlight: limits.maxLocations === -1
      });
    }

    if (limits.maxProducts !== undefined && limits.maxProducts !== null) {
      featureList.push({
        name: `${formatLimit(limits.maxProducts)} Products`,
        included: true,
        highlight: limits.maxProducts === -1
      });
    }

    if (limits.maxMonthlyOrders !== undefined && limits.maxMonthlyOrders !== null) {
      featureList.push({
        name: `${formatLimit(limits.maxMonthlyOrders)} Orders/month`,
        included: true,
        highlight: limits.maxMonthlyOrders === -1
      });
    }

    if (limits.maxStorageGb !== undefined && limits.maxStorageGb !== null) {
      featureList.push({
        name: `${formatLimit(limits.maxStorageGb)} Storage (GB)`,
        included: true,
        highlight: limits.maxStorageGb === -1
      });
    }
  }

  // Add our known feature flags
  const hasAnyFlags = features && Object.keys(features).length > 0;

  if (!hasAnyFlags) {
    // Legacy / existing plans with no explicit feature flags: treat all as enabled
    FEATURE_FLAGS.forEach((f) => {
      featureList.push({
        name: f.name,
        included: true,
        highlight: false,
      });
    });
  } else {
    FEATURE_FLAGS.forEach((f) => {
      const enabled = !!(features && features[f.id] === true);
      featureList.push({
        name: f.name,
        included: enabled,
        highlight: false,
      });
    });
  }

  return featureList;
};

/**
 * Individual Plan Card Component
 */
const SubscriptionPlanCard = ({
  plan,
  currentPlan = false,
  recommended = false,
  billingCycle = 'monthly',
  onSelectPlan,
  onUpgrade,
  onDowngrade,
  loading = false,
  disabled = false
}) => {
  const {
    id,
    planCode,
    name,
    description,
    priceMonthly,
    priceYearly,
    currency,
    trialDays,
    maxUsers,
    maxLocations,
    maxProducts,
    maxMonthlyOrders,
    maxStorageGb,
    features,
    isPopular,
    yearlyDiscountPercentage
  } = plan;

  const isYearly = billingCycle === 'yearly';
  const price = isYearly ? priceYearly : priceMonthly;
  const isFree = price === 0;

  const limits = {
    maxUsers,
    maxLocations,
    maxProducts,
    maxMonthlyOrders,
    maxStorageGb
  };

  const planFeatures = getPlanFeatures(features, limits);

  // Determine card styling
  const isHighlighted = recommended || isPopular;
  const cardElevation = isHighlighted ? 8 : currentPlan ? 4 : 2;

  const handleActionClick = () => {
    if (currentPlan) return;

    if (onSelectPlan) {
      onSelectPlan(plan, billingCycle);
    } else if (onUpgrade) {
      onUpgrade(plan);
    } else if (onDowngrade) {
      onDowngrade(plan);
    }
  };

  return (
    <Badge
      badgeContent={isPopular ? "Most Popular" : recommended ? "Recommended" : null}
      color="secondary"
      sx={{
        '& .MuiBadge-badge': {
          right: 16,
          top: 16,
          fontSize: '0.75rem',
          fontWeight: 600
        }
      }}
    >
      <Card
        elevation={cardElevation}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          border: isHighlighted ? 2 : 1,
          borderColor: isHighlighted ? 'primary.main' : 'divider',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            elevation: cardElevation + 2,
            transform: 'translateY(-4px)',
            borderColor: 'primary.main'
          },
          ...(currentPlan && {
            backgroundColor: 'primary.light',
            color: 'primary.contrastText'
          })
        }}
      >
        {/* Plan Header */}
        <CardContent sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h5" fontWeight={700}>
              {name}
            </Typography>

            {currentPlan && (
              <Chip
                label="Current Plan"
                color="primary"
                size="small"
                icon={<CheckIcon />}
              />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
            {description}
          </Typography>

          {/* Pricing */}
          <Box display="flex" alignItems="baseline" gap={1} mb={1}>
            <Typography variant="h3" fontWeight={700} color="primary">
              {formatPrice(price, currency)}
            </Typography>
            {!isFree && (
              <Typography variant="body1" color="text.secondary">
                /{isYearly ? 'year' : 'month'}
              </Typography>
            )}
          </Box>

          {/* Yearly Discount */}
          {isYearly && yearlyDiscountPercentage > 0 && (
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <TrendingUpIcon color="success" sx={{ fontSize: 16 }} />
              <Typography variant="body2" color="success.main" fontWeight={500}>
                Save {yearlyDiscountPercentage.toFixed(0)}% with yearly billing
              </Typography>
            </Box>
          )}

          {/* Trial Period - More Prominent */}
          {trialDays > 0 && !currentPlan && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                bgcolor: 'primary.light',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'primary.main'
              }}
            >
              <Typography variant="body1" color="primary" fontWeight={600} align="center">
                🎉 {trialDays}-Day Free Trial
              </Typography>
              <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 0.5 }}>
                Try all features risk-free
              </Typography>
            </Box>
          )}
        </CardContent>

        <Divider />

        {/* Features List */}
        <CardContent sx={{ flexGrow: 1, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Features Included:
          </Typography>

          {planFeatures.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No features specified
            </Typography>
          ) : (
            <List dense sx={{ py: 0, maxHeight: 400, overflow: 'auto' }}>
              {planFeatures.map((feature, index) => (
                <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {feature.included ? (
                      <CheckIcon
                        color={feature.highlight ? "primary" : "success"}
                        sx={{ fontSize: 18 }}
                      />
                    ) : (
                      <CloseIcon
                        color="disabled"
                        sx={{ fontSize: 18 }}
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={feature.name}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: feature.included ? 'text.primary' : 'text.disabled',
                      fontWeight: feature.highlight ? 600 : 400
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>

        {/* Action Button */}
        <CardActions sx={{ p: 2, pt: 0 }}>
          <Button
            fullWidth
            variant={isHighlighted ? "contained" : "outlined"}
            color="primary"
            size="large"
            onClick={handleActionClick}
            disabled={disabled || loading || currentPlan}
            startIcon={currentPlan ? <CheckIcon /> : isHighlighted ? <StarIcon /> : null}
            sx={{
              fontWeight: 600,
              py: 1.5,
              ...(isHighlighted && {
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
              })
            }}
          >
            {loading ? 'Processing...' :
              currentPlan ? 'Current Plan' :
                isFree ? 'Start Free Trial' :
                  `Choose ${name}`}
          </Button>
        </CardActions>
      </Card>
    </Badge>
  );
};

export default SubscriptionPlanCard;
